import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import history from 'store/history';

/**
 * A compatibility shim for react-router v5's `withRouter` HOC.
 * Injects `history`, `location`, and `match` props into the wrapped component
 * so that existing class components continue to work without rewriting.
 *
 * The `history` object delegates to the singleton for `listen` and `block`,
 * and to `useNavigate` for `push`, `replace`, `go`, `goBack`, `goForward`.
 *
 * @param {React.ComponentType} Component - The component to wrap with router props.
 * @return {React.ComponentType} A new component with router props injected.
 */
function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        const navigate = useNavigate();
        const location = useLocation();
        const params = useParams();

        const navigateRef = React.useRef(navigate);
        const locationRef = React.useRef(location);
        navigateRef.current = navigate;
        locationRef.current = location;

        const historyShim = React.useMemo(
            () => ({
                push: (path, state) => {
                    if (typeof path === 'object') {
                        navigateRef.current(path, { state: path.state || state });
                    } else {
                        navigateRef.current(path, { state });
                    }
                },
                replace: (path, state) => {
                    if (typeof path === 'object') {
                        navigateRef.current(path, { replace: true, state: path.state || state });
                    } else {
                        navigateRef.current(path, { replace: true, state });
                    }
                },
                go: n => navigateRef.current(n),
                goBack: () => navigateRef.current(-1),
                goForward: () => navigateRef.current(1),
                listen: history.listen.bind(history),
                block: history.block ? history.block.bind(history) : () => () => {},

                get action() {
                    return history.action;
                },
                get length() {
                    return window.history.length;
                },
                get location() {
                    return locationRef.current;
                },
                createHref: history.createHref
                    ? history.createHref.bind(history)
                    : to =>
                          typeof to === 'string'
                              ? to
                              : `${to.pathname || ''}${to.search || ''}${to.hash || ''}`,
            }),
            [] // eslint-disable-line react-hooks/exhaustive-deps
        );

        const match = React.useMemo(
            () => ({
                params,
                path: location.pathname,
                url: location.pathname,
            }),
            [params, location.pathname]
        );

        return <Component {...props} history={historyShim} location={location} match={match} />;
    }

    ComponentWithRouterProp.displayName = `withRouter(${Component.displayName ||
        Component.name ||
        'Component'})`;

    // Copy over any static methods (e.g., getInitialProps, WrappedComponent)
    if (Component.WrappedComponent) {
        ComponentWithRouterProp.WrappedComponent = Component.WrappedComponent;
    }

    return ComponentWithRouterProp;
}

export default withRouter;
